import { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react'
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import images from "../../components/images";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDownIcon } from "@heroicons/react/24/solid"

function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth()
  const [startDate, setStartDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="bg-white shadow-sm border-gray-200">
      <div className="px-6 py-3">
        <div className="flex items-center justify-between">

          {/* LEFT SECTION */}
          <div className="flex items-center gap-x-6">
            {/* Logo (Always Visible) */}
            <img
              src={images.logo}
              alt="Future Forward Logo"
              className="w-full max-w-[160px] h-[60px]"
            />

            {/* Sidebar Toggle Button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none"
            >
              <Bars3Icon className="h-[27px] w-[27px] text-primary-500" />
            </button>

            {/* Title + Date (Hidden on very small screens) */}
            <div className="hidden sm:flex items-center gap-x-2">
              {/* <h1 className="text-xl font-bold text-gray-900 whitespace-nowrap">
                Admin Dashboard
              </h1> */}

              <div className="flex items-center border rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer">
                <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="focus:outline-none cursor-pointer bg-transparent w-full max-w-[92px]"
                />
                <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
              </div>
            </div>
          </div>

          {/* RIGHT SECTION (Desktop ≥992px / lg breakpoint) */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="search applications, users, report"
                className="w-full sm:min-w-[250px] md:min-w-[289px] pl-10 pr-4 py-3 text-sm rounded-lg border border-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>

            {/* Notifications */}
            <button className="p-2 text-gray-400 hover:text-gray-500">
              <BellIcon className="h-6 w-6" />
            </button>

            {/* User menu */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 text-sm rounded-full focus:outline-none">
                <img
                  src="https://i.pravatar.cc/40"
                  alt="User Avatar"
                  className="h-[40px] w-[40px] rounded-full object-cover"
                />
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <Link to="/profile" className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}>
                        <UserCircleIcon className="mr-3 h-5 w-5" />
                        Your Profile
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link to="/settings" className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}>
                        <Cog6ToothIcon className="mr-3 h-5 w-5" />
                        Settings
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button onClick={logout} className={`${active ? 'bg-gray-100' : ''} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>

          {/* Mobile Toggle (<992px) */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          >
            {isOpen ? (
              <XMarkIcon className="h-6 w-6 text-gray-700" />
            ) : (
              <Bars3Icon className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu (<992px) */}
      {isOpen && (
        <div className="lg:hidden px-6 pb-4 space-y-4 border-t bg-white">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="search applications, users, report"
              className="w-full pl-10 pr-4 py-3 text-sm rounded-lg border border-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
          </div>

          {/* Notifications */}
          <button className="flex items-center gap-2 p-2 text-gray-600 hover:bg-gray-50 rounded-md w-full">
            <BellIcon className="h-6 w-6" /> Notifications
          </button>

          {/* User actions */}
          <div className="space-y-2">
            <Link to="/profile" className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50">
              <UserCircleIcon className="h-5 w-5" /> Profile
            </Link>
            <Link to="/settings" className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50">
              <Cog6ToothIcon className="h-5 w-5" /> Settings
            </Link>
            <button onClick={logout} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 w-full">
              <ArrowRightOnRectangleIcon className="h-5 w-5" /> Sign out
            </button>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header;
